import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Spinner } from './ui';

/** What comes out, square. Above what any avatar is ever drawn at. */
const OUT = 512;
const MAX_ZOOM = 4;

/**
 * Choosing which part of a picture becomes your face.
 *
 * The frame is the circle itself rather than a square with a circle drawn on
 * it, so what is inside the ring is exactly what everyone will see — there is
 * no second guess about how it will be cut afterwards.
 *
 * Drag to move, and a slider to zoom rather than a pinch. A pinch needs two
 * fingers on a picture small enough that both of them cover it, and a slider
 * can be nudged by someone who is trying to get one eye off the edge.
 */
export function CropPhoto({
  file,
  onCancel,
  onDone,
}: {
  file: File;
  onCancel: () => void;
  onDone: (cropped: File) => void;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [at, setAt] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const from = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const size = frame.current?.clientWidth ?? 0;
  // The scale at which the picture just covers the frame, whichever way round
  // it is. Everything else is measured from here.
  const cover =
    image && size ? Math.max(size / image.naturalWidth, size / image.naturalHeight) : 1;
  const scale = cover * zoom;
  const shown = image
    ? { w: image.naturalWidth * scale, h: image.naturalHeight * scale }
    : { w: 0, h: 0 };

  /** Keeps the picture covering the frame, so no corner is ever empty. */
  const clamp = (next: { x: number; y: number }) => ({
    x: Math.min(0, Math.max(size - shown.w, next.x)),
    y: Math.min(0, Math.max(size - shown.h, next.y)),
  });

  // A new zoom moves the edges, so whatever was offset may now be out of
  // bounds. Re-clamped rather than reset: the point of interest stays put.
  useEffect(() => setAt((current) => clamp(current)), [zoom, image, size]);

  const onDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    from.current = { x: event.clientX - at.x, y: event.clientY - at.y };
  };

  const onMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = from.current;
    if (!start) return;
    setAt(clamp({ x: event.clientX - start.x, y: event.clientY - start.y }));
  };

  const onUp = () => {
    from.current = null;
  };

  const cut = async () => {
    if (!image || !size) return;
    setBusy(true);

    const canvas = document.createElement('canvas');
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    /*
     * What the frame is showing, in the picture's own pixels. The frame is
     * `size` across on screen and the picture is drawn at `scale`, so the
     * visible square is that much smaller in the original.
     */
    const span = size / scale;
    ctx.drawImage(image, -at.x / scale, -at.y / scale, span, span, 0, 0, OUT, OUT);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9),
    );
    if (!blob) {
      setBusy(false);
      return;
    }

    onDone(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
  };

  return (
    <div className="crop">
      <div className="crop-head">
        <button className="btn btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <span className="grow" />
        <button className="btn" onClick={() => void cut()} disabled={!image || busy}>
          {busy ? 'Saving…' : 'Use this'}
        </button>
      </div>

      <div
        ref={frame}
        className="crop-frame"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        {image ? (
          <img
            src={image.src}
            alt=""
            draggable={false}
            style={{
              width: shown.w,
              height: shown.h,
              transform: `translate(${at.x}px, ${at.y}px)`,
            }}
          />
        ) : (
          <span className="center" style={{ width: '100%', height: '100%' }}>
            <Spinner />
          </span>
        )}
      </div>

      <label className="crop-zoom">
        <span className="sr-only">Zoom</span>
        <input
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
        />
      </label>

      <span className="tiny dim" style={{ textAlign: 'center' }}>
        Drag to move it about. This circle is exactly what everyone sees.
      </span>
    </div>
  );
}
